import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { companySubscriptionService } from "../services/companySubscription.service";

const errMsg = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.detail || fallback;

export const fetchSubscriptionsByCompany = createAsyncThunk(
  "companySubscription/fetchByCompany",
  async (companyId, thunkAPI) => {
    try {
      return await companySubscriptionService.getSubscriptionsByCompany(companyId);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch subscriptions");
    }
  }
);

export const fetchActiveSubscription = createAsyncThunk(
  "companySubscription/fetchActive",
  async (companyId, thunkAPI) => {
    try {
      return await companySubscriptionService.getActiveSubscription(companyId);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch active subscription");
    }
  }
);

export const addSubscription = createAsyncThunk(
  "companySubscription/add",
  async (subscription, thunkAPI) => {
    try {
      return await companySubscriptionService.createSubscription(subscription);
    } catch (err) {
      return thunkAPI.rejectWithValue(errMsg(err, "Failed to create subscription"));
    }
  }
);

export const editSubscription = createAsyncThunk(
  "companySubscription/edit",
  async ({ id, subscription }, thunkAPI) => {
    try {
      return await companySubscriptionService.updateSubscription(id, subscription);
    } catch (err) {
      return thunkAPI.rejectWithValue(errMsg(err, "Failed to update subscription"));
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  "companySubscription/cancel",
  async ({ id, reason }, thunkAPI) => {
    try {
      return await companySubscriptionService.cancelSubscription(id, reason);
    } catch (err) {
      return thunkAPI.rejectWithValue(errMsg(err, "Failed to cancel subscription"));
    }
  }
);

const companySubscriptionSlice = createSlice({
  name: "companySubscription",
  initialState: {
    subscriptions: [],
    activeSubscription: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionsByCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionsByCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload;
      })
      .addCase(fetchSubscriptionsByCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchActiveSubscription.fulfilled, (state, action) => {
        state.activeSubscription = action.payload;
      })
      .addCase(addSubscription.fulfilled, (state, action) => {
        state.subscriptions.unshift(action.payload);
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        const targetId = action.meta.arg.id;
        const sub = state.subscriptions.find(
          (s) => s.subscriptionId === targetId
        );
        if (sub) {
          sub.status = 3; // Cancelled
          sub.cancellationDate = new Date().toISOString();
          sub.cancellationReason = action.meta.arg.reason;
        }
      });
  },
});

export default companySubscriptionSlice.reducer;
